import React from 'react';
import { motion } from 'framer-motion';
import EnterpriseVideo from './EnterpriseVideo';

/**
 * TripleVideoHero - Hero section with 3 side-by-side videos.
 *
 * Each video renders through the shared EnterpriseVideo component, so it
 * gets the standardized Enterprise Solutions video controls (play/pause,
 * mute, fullscreen) with discreet hover-to-reveal behavior, autoplay on
 * ≥50% visibility, and automatic pause when scrolled out of view. The
 * gold-bordered frame, inset shadow and layout are preserved exactly.
 */
const TripleVideoHero = ({ title, description, videos }) => {
  return (
    <section className="relative min-h-[85vh] overflow-hidden bg-white">
      {/* Video Container - 3 videos side by side with gaps and frames */}
      <div className="absolute inset-0 flex flex-col md:flex-row gap-3 md:gap-4 p-3 sm:p-4 md:p-6">
        {videos.map((videoUrl, index) => (
          <div
            key={index}
            className="relative flex-1 rounded-lg overflow-hidden min-h-[180px] sm:min-h-[220px] md:min-h-0"
            style={{
              border: '2px solid #D4AF37',
              boxShadow:
                'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            {/* Inner padding/frame effect */}
            <div className="absolute inset-0 p-1">
              <EnterpriseVideo
                videoSrc={videoUrl}
                title={`Enterprise hero video ${index + 1}`}
                testId={`triple-video-${index}`}
                className="w-full h-full rounded-md"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Gradient Overlay for Text Readability */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 md:via-transparent to-black/60"
        style={{ pointerEvents: 'none' }}
      />

      {/* Hero Text Content - Overlaid on Videos */}
      <div className="relative z-10 flex items-center justify-center min-h-[85vh] px-4 sm:px-6 lg:px-8 py-12 md:py-0">
        <div className="text-center max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1
              className="text-4xl sm:text-5xl md:text-6xl font-medium mb-4 md:mb-6"
              style={{
                color: 'white',
                textShadow: '0 2px 20px rgba(0, 0, 0, 0.8)',
              }}
            >
              {title}
            </h1>
            <p
              className="text-base sm:text-lg md:text-xl max-w-3xl mx-auto"
              style={{
                color: 'rgba(255, 255, 255, 0.95)',
                textShadow: '0 2px 15px rgba(0, 0, 0, 0.8)',
              }}
            >
              {description}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TripleVideoHero;
